<template>
	<div class="mainWrapper direct-purchase">
		<h1>PO CSV Import</h1>
		<div class="form" v-if="suppliers.length > 0">
			<div>
				<span class="label">SUPPLIER</span>
				<b-autocomplete
					v-model="supplierSuggestions"
					:data="filteredSuppliers"
					:open-on-focus="true"
					placeholder="Supplier"
					field="name"
					@select="option => (selectedSupplier = option)"
					class="brandAutocomplete"
					:disabled="selectedSupplier"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</div>

			<div v-if="selectedSupplier && brands.length > 0">
				<span class="label">BRAND</span>
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="name"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
					:disabled="selectedBrand"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</div>

			<div v-if="selectedBrand">
				<span class="label">UPLOAD FILE</span>
				<b-field class="file">
					<input type="file" @change="loadTextFromFile" />
				</b-field>
			</div>

			<div v-if="product">
				<span class="label">ADD</span>
				<b-button type="is-info" icon-left="plus-circle-outline" @click="submit">ADD PRODUCT</b-button>
			</div>
		</div>

		<div class="form">
			<div>
				<span class="label">ORDER DATE</span>
				<b-datepicker :date-formatter="dateFormatter" v-model="orderDate" icon="calendar-today" placeholder="Order Date"> </b-datepicker>
			</div>

			<div>
				<span class="label">EXPECTED DATE</span>
				<b-datepicker :date-formatter="dateFormatter" v-model="expectedDate" icon="calendar-today" placeholder="Expected Date"> </b-datepicker>
			</div>
		</div>

		<div class="list">
			<!-- v-if="products.length > 0" -->
			<div class="products-list header">
				<div>
					BD CODE
				</div>

				<div>
					BARCODE
				</div>

				<div>
					NAME
				</div>

				<div>
					PACKAGING
				</div>

				<div>
					QUANTITY
				</div>
			</div>

			<div v-for="(item, index) of products" v-bind:key="index" class="products-list">
				<div>
					{{ item.bd_code }}
				</div>

				<div>
					{{ item.barcode }}
				</div>

				<div>
					{{ item.name }}
				</div>

				<div>
					<b-dropdown :value="item.packaging_selection" :disabled="true" aria-role="list">
						<template #trigger>
							<b-button :label="item.packaging_selection || 'Select'" type="is-primary" :disabled="true" icon-right="menu-down" />
						</template>

						<b-dropdown-item v-for="option in item.availablePackagingOptions" :key="option.value" :value="option.value" aria-role="listitem">
							{{ option.label }}
						</b-dropdown-item>
					</b-dropdown>
				</div>

				<div>
					<input name="qtyInput" class="qtyInput" type="number" v-model="item.qty" disabled />
				</div>
			</div>
		</div>

		<div v-if="products.length > 0" class="detail-buttons-wrapper">
			<div></div>
			<div class="align-right">
				<button class="big-button" @click="save()">SAVE</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'POCSVImport',
	data: function() {
		return {
			warehouse: null,
			suppliers: [], // List of suppliers from API
			selectedSupplier: null, // Selected supplier
			supplierSuggestions: '', // Supplier autocomplete suggestions
			brands: [], // List of brands from API
			selectedBrand: null, // Selected brand
			bdcodeData: [], // API bdcode data
			searchData: [], // API Search data
			brandSuggestions: '', // List of brands
			product: null, // 1 selected product
			products: [], // Selected products
			name: null,
			content: null,
			orderDate: null, // Global order date
			expectedDate: null // Global expected date
		}
	},
	computed: {
		filteredSuppliers() {
			return this.suppliers.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.supplierSuggestions.toLowerCase()) >= 0
				)
			})
		},
		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	watch: {
		content: function() {
			this.verifyContent()
		},

		selectedSupplier: function(newData) {
			this.selectedBrand = null
			this.product = null
			this.products = []
			this.brands = []

			if (newData) {
				// Load brands for selected supplier
				this.brands = newData.brand || []
			}
		},

		selectedBrand: function(newData) {
			this.product = null
			this.products = []
		}
	},

	mounted: async function() {
		// Get current warehouse info
		const warehouseData = {
			populate: ['suppliers']
		}
		const apiGetWarehouse = await this.$api.getWarehouse({ data: warehouseData })
		this.warehouse = apiGetWarehouse.data

		// Get list of suppliers with brands populated
		const supplierData = {
			populate: ['brand']
		}
		const apiGetSuppliers = await this.$api.getSuppliers({ data: supplierData })
		this.suppliers = apiGetSuppliers.data.list || []
	},

	methods: {
		verifyContent: async function() {
			for (var transfer of this.content) {
				const bdCode = transfer.bdCode
				const qty = transfer.qty
				const packaging = transfer.packaging || ''

				const data = {
					brand: this.selectedBrand._id,
					bd_code: bdCode
				}

				const apiProductDetail = await this.$api.searchProduct({ data })

				if (!apiProductDetail || apiProductDetail.data.count == 0) {
					this.$swal.fire('Error!', bdCode + ' is not in ' + this.selectedBrand.name + ' brand!', 'error')
					this.error = true
					return
				}

				let apiProduct = apiProductDetail.data.list[0]

				const duplicate = this.products.filter(product => {
					return product._id == apiProduct._id
				})

				if (duplicate.length > 0) {
					this.$swal.fire('Duplicate!', bdCode + ' is already added!', 'error')
					this.error = true
					return
				}

				// Set basic product data
				apiProduct.qty = qty

				// Set packaging selection based on product.original_price_unit or default to single_item
				apiProduct.packaging_selection = apiProduct.original_price_unit || 'single_item'

				this.products.push(apiProduct)
			}
		},

		// Load CSV and add into array
		loadTextFromFile(ev) {
			this.error = false
			const file = ev.target.files[0]
			const reader = new FileReader()

			let error = false
			let productData = []

			// reader.onload = e => this.$emit("load", e.target.result);
			reader.onload = event => {
				var raw = event.target.result.split(/\r?\n/)

				// Get header row and create column mapping
				const headerRow = raw[0]
				const headers = headerRow.split(/,/).map(h => h.trim().toUpperCase())

				// Expected headers: BD CODE, QUANTITY, PACKAGING
				const bdCodeIndex = headers.indexOf('BD CODE')
				const quantityIndex = headers.indexOf('QUANTITY')
				const packagingIndex = headers.indexOf('PACKAGING')

				// Validate required columns exist
				if (bdCodeIndex === -1 || quantityIndex === -1) {
					this.$swal.fire('Error!', 'CSV must contain columns: BD CODE, QUANTITY, PACKAGING', 'error')
					return
				}

				// Remove header row
				raw.shift()

				// Loop every row to put it in array
				raw.map(data => {
					if (!data.trim()) return // Skip empty rows

					var splitData = data.split(/,/)
					var newData = {}

					newData.bdCode = splitData[bdCodeIndex] ? splitData[bdCodeIndex].trim() : ''
					newData.qty = splitData[quantityIndex] ? splitData[quantityIndex].trim() : ''
					newData.packaging = packagingIndex >= 0 && splitData[packagingIndex] ? splitData[packagingIndex].trim() : ''

					// Validate required fields
					if (!newData.bdCode) {
						return // Skip rows without BD CODE
					}

					if (!newData.qty || newData.qty == '') {
						this.$swal.fire('Error!', newData.bdCode + ' quantity cannot be blank', 'error')
						error = true
						return
					}

					if (newData.bdCode) {
						productData.push(newData)
					}
				})

				if (error == false) {
					this.content = productData
				}
			}

			reader.readAsText(file)
		},

		save: async function() {
			// Validate required fields
			if (!this.orderDate) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Order Date is required'
				})
				return
			}

			if (!this.expectedDate) {
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Expected Date is required'
				})
				return
			}

			var newData = []

			this.products.map(product => {
				var productData = {}

				productData.product = product._id
				productData.order_date = this.$moment(this.orderDate).format('DD/MM/YYYY')
				productData.expected_date = this.$moment(this.expectedDate).format('DD/MM/YYYY')
				productData.currency = 'MMK'
				productData.qty = product.qty

				// Add packaging selection for each product
				productData.packaging_selection = product.packaging_selection || 'single_item'

				newData.push(productData)
			})

			const data = {
				supplier: this.selectedSupplier._id,
				brand: this.selectedBrand._id,
				warehouse: this.warehouse._id,
				purchase_order_items: newData,
				type: 'warehouse_to_supplier'
			}

			const apiCreatePO = await this.$api.createPurchaseOrder({ data })

			if (apiCreatePO !== undefined) {
				this.$swal.fire(apiCreatePO.data.purchase_order.code)

				this.$router.push('/po')
			}
		},

		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		},

		submit: async function() {
			this.product.received_date = null
			this.product.qty = null
			this.product.remark = null

			this.products.push(this.product)

			this.product = null
			this.searchData = []
		},

		update: function(index, item, value) {
			if (item == 'qty') {
				this.products[index].qty = Number(value)
			}

			// if (item == "price") {
			// 	this.products[index].price = Number(value);
			// }
		},

		// Autocomplete search
		searchProducts: async function(name) {
			if (name.length > 1) {
				const data = {
					brand: this.selectedBrand._id,
					bd_code: name
				}

				const apiSearchProduct = await this.$api.searchProduct({ data })

				this.searchData = apiSearchProduct.data.list
			}
		}
	}
}
</script>

<style scoped>
.transfer-date {
	width: 300px;
	margin-bottom: 2em;
}

.form {
	width: 500px;
	text-align: left;
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr;
	grid-gap: 20px;
	width: 100%;
	margin-bottom: 40px;
}

span.label {
	font-size: 12px;
	font-weight: bold;
	color: #ccc;
	text-align: left;
	margin-bottom: 10px;
}

/* .products-list {
	margin-bottom: 20px;
	display: grid;

	grid-gap: 20px;
	width: 100%;
	text-align: left;
	font-weight: bold;
} */

.products-list {
	margin: auto;
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 0.8fr;
	grid-gap: 10px;
	padding: 20px 5px;
	font-weight: bold;
	font-size: 14px;
	color: #000;
	border-bottom: 1px solid #eee;
}

.header {
	color: #aaa;
	font-weight: bold;
	font-size: 14px;
	margin-bottom: 10px;
}

.qtyInput {
	width: 90px;
	height: 30px;
	border: 0;
	text-align: center;
	border-radius: 5px;
	padding-left: 10px;
	font-size: 14px;
	font-weight: bold;
	color: #000;
	background-color: #f5f5f5;
	/* background: #70a15a; */
}

.priceInput {
	width: 150px;
	height: 30px;
	border: 0;
	text-align: center;
	border-radius: 5px;
	padding-left: 10px;
	font-size: 14px;
	font-weight: bold;
	color: #000;
	background-color: #f5f5f5;
}

.remark {
	width: 100%;
	height: 50px;
	border-radius: 4px;
	border: 1px solid #ccc;
	background-color: #fff;
	padding: 5px;
	color: #000;
	font-size: 14px;
	overflow-wrap: break-word;
}
</style>
